import t from 'react-translate';
import { useContext } from 'react';
import { css } from '@emotion/react';

import { SessionTypes } from 'redux/schemas/models/live-event';
import {
  sessionTypeOptions,
  translationIntegrationKeys,
} from 'redux/schemas/models/mentoring-program-sessions';

import { gray4, gray2 } from 'styles/global_defaults/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';

import prodPathReplace from 'shared/prod-path-rewrite';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { tooltipZIndex } from 'athena/components/constants';

import { config } from '@config/pendo.config.json';

const getStyles = (color: string) => css`
  border-radius: ${quarterSpacing}px;

  .join-meeting-text {
    a {
      color: ${color};
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .remove-integration {
    color: ${gray4};
    cursor: pointer;
    &:hover {
      color: ${gray2};
    }
  }
  .line {
    transform: rotate(90deg);
    color: ${gray4};
  }
`;

type JoinMeetingButtonProps = {
  type: SessionTypes;
  link: string;
  onRemove: () => void;
};

const JoinMeetingButton = (props: JoinMeetingButtonProps) => {
  const { type, link, onRemove } = props;
  const { img, color } = sessionTypeOptions[type];

  const translations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.VIRTUAL_MEETING.JOIN_VIRTUAL_MEETING;
  const translationKey = translationIntegrationKeys[type];
  const joinMeetingText = translations[translationKey]();

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const dataQa = config.pendo.athena.mentorshipProgram.participantHome.sessions.planSessionFlyout.virtualMeeting.joinMeeting;
  const dataQaId = `${dataQa}_${mentoringProgram?.id}`;

  return (
    <div css={getStyles(color)} className='d-flex align-items-center p-1 bg-gray-6'>
      <ClickableContainer
        className='d-flex align-items-center join-meeting-text semi-bold'
      >
        <img
          src={prodPathReplace(img)}
          alt={translationKey}
          className='mr-4'
        />
        {link ? (
          <a
            href={link}
            target='_blank'
            rel='noopener noreferrer'
            data-qa={dataQa}
            data-qa-id={dataQaId}
            aria-label={joinMeetingText}
          >
            {joinMeetingText}
          </a>
        ) : (
          <NvTooltip
            text={translations.TOOLTIP.MEETING_TEXT()}
            placement='top'
            zIndex={tooltipZIndex}
          >
            <span>{joinMeetingText}</span>
          </NvTooltip>
        )}
      </ClickableContainer>
      <NvIcon icon='dash' size='smallest' className='ml-2 line' />
      <NvTooltip
        text={translations.REMOVE_VIRTUAL_MEETING()}
        placement='top'
        zIndex={tooltipZIndex}
      >
        <div className='ml-2 mr-1 justify-content-center'>
          <NvIcon
            icon='close'
            size='smallest'
            className='remove-integration'
            onClick={onRemove}
            altLabel={translations.REMOVE_VIRTUAL_MEETING()}
          />
        </div>
      </NvTooltip>
    </div>
  );
};

export default JoinMeetingButton;
